import React from 'react';
import { useLoaderData } from '@remix-run/react';
import type { loader as homeLoader } from '~/routes/_app._index';
import { ReactComponent as ArrowRight } from '@rocco/icons/svg/arrow/arrow-right.svg';
import { HomeCarouselSection } from '@rocco/ui/layout/components/HomeCarouselSection';
import { cn } from '@rocco/utils/cn';
import { Image } from '@rocco/components/image';
import { Button as RoccoButton } from '@rocco/components/button';
import { useSetLoginModal } from '~/modules-rocco/user/hook/use-open-login-modal';
import { useTracker } from '@archipro-website/tracker';
import type { TrackingEvent } from '@archipro-website/tracker';
export const HomePageMarketingGuides = () => {
    const { HomeContentCampaign, appDisplayMode, isLoggedIn } =
        useLoaderData<typeof homeLoader>();

    const { setLoginModal } = useSetLoginModal();
    const tracker = useTracker();
    if (!HomeContentCampaign) {
        return null;
    }

    const log = (eventName: TrackingEvent, guideId?: string) => {
        if (!guideId) {
            tracker.log(eventName, {
                data: {
                    extraData: {
                        sectionName: 'HomePageGuides',
                    },
                },
                url: new URL(window.location.href),
            });
            return;
        }
        tracker.log(eventName, {
            data: {
                extraData: {
                    sectionName: 'HomePageGuides',
                    guideId: guideId,
                },
            },
            url: new URL(window.location.href),
        });
    };

    const stepToId = (step: number) => {
        switch (step) {
            case 1:
                return 'Guide1-NZ.pdf';
            case 2:
                return 'Guide2-NZ.pdf';
            case 3:
                return 'Guide3-NZ.pdf';
            default:
                return 'unknown';
        }
    };

    const trackWatchSeries = () => log('WatchSeries');

    return (
        <section
            className="bg-brick-700 bg-cover bg-center bg-no-repeat py-20 md:py-55"
            style={
                HomeContentCampaign?.backgroundImage
                    ? {
                          backgroundImage: `url(${HomeContentCampaign?.backgroundImage})`,
                      }
                    : {}
            }
        >
            <HomeCarouselSection
                displayMode={appDisplayMode}
                title={HomeContentCampaign?.title}
                description={HomeContentCampaign?.description}
                theme="dark"
                ctaButton={HomeContentCampaign?.cta}
                showLoginModal={() =>
                    setLoginModal({
                        isOpen: true,
                        authSource: 'homeModule',
                        redirect: HomeContentCampaign?.cta?.link,
                    })
                }
                trackClick={trackWatchSeries}
            >
                {HomeContentCampaign?.guides?.map((item, index) => (
                    <div
                        key={`guides-${index}`}
                        className={cn(
                            'flex aspect-[280/350] flex-col rounded-2 border-[2px] border-[#c6c6c6] bg-gray-900/20 p-4.5 backdrop-blur-[7.5px] transition-colors duration-300 md:aspect-[594/690] md:h-[43.125rem] md:pb-12 md:pl-15.5 md:pr-[2.8125rem] md:pt-12.5'
                        )}
                    >
                        <div className="flex flex-row justify-between pb-[2.375rem] md:pb-[4.1875rem]">
                            <div className="relative flex aspect-[200/225] w-50 items-center justify-center overflow-hidden bg-gray-300">
                                {item.image ? (
                                    <Image
                                        src={item.image}
                                        alt={item.description}
                                        fill={true}
                                        loading="lazy"
                                        fit="cover"
                                        width={200}
                                        height={225}
                                    />
                                ) : null}
                            </div>

                            <span className="hidden text-xl text-white md:inline md:text-[15.625rem] md:leading-[12.5rem]">
                                {item.step}
                            </span>
                        </div>
                        <div className="flex flex-col gap-4 md:gap-12">
                            <span className="h-30 pb-[0.35rem] text-sm leading-[142.85%] text-white md:h-[11.5rem] md:min-h-[11.5rem] md:w-[23.5rem] md:text-lg md:leading-[121.43%]">
                                {item.description}
                            </span>

                            <div className="mt-4 md:mt-auto">
                                <RoccoButton
                                    color={'white'}
                                    inverted
                                    asChild
                                    clickAnimation="scale"
                                    onClick={() => {
                                        log(
                                            'DownloadFreeClick',
                                            stepToId(item.step)
                                        );
                                        if (!isLoggedIn) {
                                            setLoginModal({
                                                isOpen: true,
                                                authSource: 'homeModule',
                                                redirect: item.link,
                                                title: 'Continue to Download',
                                            });
                                        }
                                    }}
                                >
                                    {isLoggedIn ? (
                                        <a
                                            href={item.link}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                        >
                                            {`Download (Free)`}
                                            <ArrowRight className="ml-1.5 w-4" />
                                        </a>
                                    ) : (
                                        <div className="flex flex-row items-center">
                                            {`Download (Free)`}
                                            <ArrowRight className="ml-1.5 w-4" />
                                        </div>
                                    )}
                                </RoccoButton>
                            </div>
                        </div>
                    </div>
                ))}
            </HomeCarouselSection>
        </section>
    );
};
